/**
 * Субъекты РФ: meta + GeoJSON полигоны, point-in-polygon, фильтр АЗС.
 * Ассеты: public/geo/russia-regions-meta.json, public/geo/russia-regions.geojson
 */

const META_URL = `${process.env.BASE_URL || '/'}geo/russia-regions-meta.json`
const GEO_URL = `${process.env.BASE_URL || '/'}geo/russia-regions.geojson`

let _metaPromise = null
let _geoPromise = null
/** @type {Map<string, object>|null} */
let _featureById = null

function isFiniteCoordPair(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false
  if (lat === 0 && lng === 0) return false
  return true
}

/**
 * Ray casting: точка [lng, lat] внутри кольца (ring = [[lng,lat],...]).
 * Граница считается внутри.
 */
export function pointInRing(lng, lat, ring) {
  if (!Array.isArray(ring) || ring.length < 3) return false
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = Number(ring[i][0])
    const yi = Number(ring[i][1])
    const xj = Number(ring[j][0])
    const yj = Number(ring[j][1])
    if (!Number.isFinite(xi) || !Number.isFinite(yi) || !Number.isFinite(xj) || !Number.isFinite(yj)) {
      continue
    }
    // на ребре
    const onEdge =
      Math.min(yi, yj) <= lat &&
      lat <= Math.max(yi, yj) &&
      Math.min(xi, xj) <= lng &&
      lng <= Math.max(xi, xj) &&
      Math.abs((xj - xi) * (lat - yi) - (lng - xi) * (yj - yi)) < 1e-12
    if (onEdge) return true

    const intersect =
      yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi + 0.0) + xi
    if (intersect) inside = !inside
  }
  return inside
}

/** Polygon: первый ring — exterior, остальные — holes */
export function pointInPolygonCoords(lng, lat, polygonCoords) {
  if (!Array.isArray(polygonCoords) || !polygonCoords.length) return false
  if (!pointInRing(lng, lat, polygonCoords[0])) return false
  for (let h = 1; h < polygonCoords.length; h++) {
    if (pointInRing(lng, lat, polygonCoords[h])) return false
  }
  return true
}

export function pointInGeometry(lng, lat, geometry) {
  if (!geometry || !isFiniteCoordPair(lat, lng)) return false
  if (geometry.type === 'Polygon') {
    return pointInPolygonCoords(lng, lat, geometry.coordinates)
  }
  if (geometry.type === 'MultiPolygon') {
    return (geometry.coordinates || []).some(poly =>
      pointInPolygonCoords(lng, lat, poly)
    )
  }
  return false
}

export function pointInBBox(lng, lat, bbox) {
  if (!Array.isArray(bbox) || bbox.length < 4) return true
  const [minLng, minLat, maxLng, maxLat] = bbox.map(Number)
  if (
    ![minLng, minLat, maxLng, maxLat].every(Number.isFinite)
  ) {
    return true
  }
  // Чукотка / антимеридиан: bbox может пересекать ±180
  if (minLng <= maxLng) {
    return lng >= minLng && lng <= maxLng && lat >= minLat && lat <= maxLat
  }
  return (lng >= minLng || lng <= maxLng) && lat >= minLat && lat <= maxLat
}

export function geometryBBox(geometry, fallbackBBox) {
  if (Array.isArray(fallbackBBox) && fallbackBBox.length >= 4) {
    return fallbackBBox.map(Number)
  }
  let minLng = Infinity
  let minLat = Infinity
  let maxLng = -Infinity
  let maxLat = -Infinity
  function walk(c) {
    if (typeof c[0] === 'number') {
      const lng = Number(c[0])
      const lat = Number(c[1])
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return
      if (lng < minLng) minLng = lng
      if (lng > maxLng) maxLng = lng
      if (lat < minLat) minLat = lat
      if (lat > maxLat) maxLat = lat
      return
    }
    ;(c || []).forEach(walk)
  }
  if (geometry?.coordinates) walk(geometry.coordinates)
  if (!Number.isFinite(minLng)) return null
  return [minLng, minLat, maxLng, maxLat]
}

export async function loadRegionsMeta() {
  if (!_metaPromise) {
    _metaPromise = fetch(META_URL)
      .then(res => {
        if (!res.ok) throw new Error(`Не удалось загрузить список регионов (${res.status})`)
        return res.json()
      })
      .then(data => {
        const regions = Array.isArray(data?.regions) ? data.regions : []
        return regions
          .filter(r => r && r.id && r.name)
          .map(r => ({
            id: String(r.id),
            name: String(r.name),
            bbox: Array.isArray(r.bbox) ? r.bbox.map(Number) : null
          }))
          .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
      })
      .catch(err => {
        _metaPromise = null
        throw err
      })
  }
  return _metaPromise
}

async function loadFeatureIndex() {
  if (_featureById) return _featureById
  if (!_geoPromise) {
    _geoPromise = fetch(GEO_URL)
      .then(res => {
        if (!res.ok) throw new Error(`Не удалось загрузить границы регионов (${res.status})`)
        return res.json()
      })
      .then(fc => {
        const map = new Map()
        for (const f of Array.isArray(fc?.features) ? fc.features : []) {
          const id = String(f?.properties?.id || '').trim()
          if (!id || !f.geometry) continue
          map.set(id, f)
        }
        _featureById = map
        return map
      })
      .catch(err => {
        _geoPromise = null
        throw err
      })
  }
  return _geoPromise
}

export async function loadRegionFeature(regionId) {
  const id = String(regionId || '').trim()
  if (!id) return null
  const index = await loadFeatureIndex()
  return index.get(id) || null
}

/**
 * @param {Array} apiList — сырой station-coordinate
 * @param {object} feature — GeoJSON Feature региона
 * @param {(item: object) => object|null} mapStation — mapApiStationCoordinate
 */
export function filterStationsByRegionFeature(apiList, feature, mapStation) {
  if (!feature?.geometry || typeof mapStation !== 'function') return []
  const bbox = geometryBBox(feature.geometry, feature.bbox)
  const result = []
  for (const raw of Array.isArray(apiList) ? apiList : []) {
    const station = mapStation(raw)
    if (!station?.coords) continue
    const [lat, lng] = station.coords
    if (!isFiniteCoordPair(lat, lng)) continue
    if (bbox && !pointInBBox(lng, lat, bbox)) continue
    if (!pointInGeometry(lng, lat, feature.geometry)) continue
    result.push(station)
  }
  result.sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || ''), 'ru')
  )
  return result
}

/**
 * Объединение bbox: [minLng, minLat, maxLng, maxLat].
 * Если хотя бы один bbox пересекает антимеридиан (minLng > maxLng) —
 * сохраняем «перевёрнутый» lng (fitBounds на карте обрабатывает отдельно).
 */
export function unionBBoxes(bboxes) {
  const valid = []
  for (const bbox of Array.isArray(bboxes) ? bboxes : []) {
    if (!Array.isArray(bbox) || bbox.length < 4) continue
    const nums = bbox.slice(0, 4).map(Number)
    if (!nums.every(Number.isFinite)) continue
    valid.push(nums)
  }
  if (!valid.length) return null

  let minLat = Infinity
  let maxLat = -Infinity
  for (const [, minB, , maxB] of valid) {
    if (minB < minLat) minLat = minB
    if (maxB > maxLat) maxLat = maxB
  }

  const antimeridian = valid.filter(([minLng, , maxLng]) => minLng > maxLng)
  if (antimeridian.length) {
    let minLng = Infinity
    let maxLng = -Infinity
    for (const [a, , c] of antimeridian) {
      if (a < minLng) minLng = a
      if (c > maxLng) maxLng = c
    }
    return [minLng, minLat, maxLng, maxLat]
  }

  let minLng = Infinity
  let maxLng = -Infinity
  for (const [a, , c] of valid) {
    if (a < minLng) minLng = a
    if (c > maxLng) maxLng = c
  }
  return [minLng, minLat, maxLng, maxLat]
}

/**
 * АЗС хотя бы в одном из полигонов; дедуп по station.id (без id — все остаются).
 */
export function filterStationsByRegionFeatures(apiList, features, mapStation) {
  const prepared = (Array.isArray(features) ? features : [])
    .filter(f => f?.geometry)
    .map(f => ({
      geometry: f.geometry,
      bbox: geometryBBox(f.geometry, f.bbox)
    }))
  if (!prepared.length || typeof mapStation !== 'function') return []

  const byId = new Map()
  const withoutId = []
  for (const raw of Array.isArray(apiList) ? apiList : []) {
    const station = mapStation(raw)
    if (!station?.coords) continue
    const [lat, lng] = station.coords
    if (!isFiniteCoordPair(lat, lng)) continue
    const inside = prepared.some(({ geometry, bbox }) => {
      if (bbox && !pointInBBox(lng, lat, bbox)) return false
      return pointInGeometry(lng, lat, geometry)
    })
    if (!inside) continue
    if (station.id != null && station.id !== '') {
      const key = String(station.id)
      if (!byId.has(key)) byId.set(key, station)
    } else {
      withoutId.push(station)
    }
  }
  const result = [...byId.values(), ...withoutId]
  result.sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || ''), 'ru')
  )
  return result
}

/** Сброс кэшей — только для тестов */
export function __resetRussiaRegionsCacheForTests() {
  _metaPromise = null
  _geoPromise = null
  _featureById = null
}
